import * as React from 'react';
import { find, flow, forEach, get } from 'lodash';
import { formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { Form } from 'react-bootstrap';
import { IAuthState } from '../../../../services/auth/models';
import { IChannel, IDocument, IOptionMap, IPremium, IProduct, IQuotes, ITraveller } from '../../../../data/api/models';
import { ValidationFailed } from '../Components/ValidationFailed';
import { II18nMessages } from '../../../../services/branding/models';
import { bindActionCreators } from 'redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { InfoAnnie } from '../../components/InfoAnnie';
import { LeadTravellerDetails } from './component/LeadTravellerDetails';
import { ImportantStatements } from './component/ImportantStatements';
import { HazardousActivities } from './component/HazardousActivities';
import { MedicalHistory } from './component/MedicalHistory';
import { QuoteStepButtons } from '../Components/QuoteStepButtons';

interface IYourQuoteFormProps extends InjectedFormProps {
  auth: IAuthState;
  travellers: ITraveller[];
  tripType: string;
  quotes: IQuotes;
  groupType: string;
  region: string;
  premiums: IPremium[],
  schemeId: string,
  options: IOptionMap,
  destinations: string[];
  product: IProduct;
  i18n: II18nMessages;
  synchronousError: any;
  coverLevel: string;
  channel: IChannel;
  quoteState: IQuoteState;
  isInstaquote: boolean;
  documents: IDocument[];
  medicalHistory: boolean;
  quotationDeclaration: boolean;
  medicalDeclaration: any[];
  onBack: () => any;
  actions: {
    quote: IQuoteActions;
    submit(name: string);
  }

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

class AboutTheTravellers extends React.Component<IYourQuoteFormProps, any> {

  constructor(props) {
    super(props);
    this.checkMedicalQuestions();
  }

  private checkMedicalQuestions = () => {
    const { medicalDeclaration, change } = this.props;
    let hasSomeYesAnswer = null;
    let hasAllFalseAnswer = null;

    forEach(medicalDeclaration, (value, key) => {
      if (get(value, 'answer')) {
        hasSomeYesAnswer = true;
      }

      if (get(value, 'answer') === false) {
        hasAllFalseAnswer = true;
      }

    })

    if (hasSomeYesAnswer) {
      change('medicalHistory', false)
    }

    if (!hasSomeYesAnswer && hasAllFalseAnswer) {
      change('medicalHistory', true)
    }
  }

  private getSelectedPremium = (): any => {
    const { actions, schemeId, tripType } = this.props;
    return actions.quote.getQuoteSelectedPremium(schemeId, tripType);
  }

  private getWordingUrl = (): string => {
    const { documents } = this.props;
    let wording;

    wording = find(documents, ['name', 'Policy Wording']);
    let path = get(wording, 'filePath', null);

    if (!wording) {
      wording = get(this.getSelectedPremium(), 'scheme.documents.Policy Wording')
      if (wording) {
        path = wording;
      }
    }

    return path;
  }

  public render() {
    const {
      handleSubmit,
      valid,
      onBack,
      submitFailed,
      submitting,
      quotationDeclaration,
      medicalHistory,
      options,
      synchronousError,
      actions
    } = this.props

    const declarationIsFalse = (typeof quotationDeclaration === 'boolean' && quotationDeclaration === false)
    const disableNext = get(options, 'hazardous_activities') === true || declarationIsFalse;

    return (
      <>
        <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">

          <div className="steps-container">

            <div className="header-title-container">
              {submitFailed && !valid && (
                <ValidationFailed/>
              )}
              <div className={'title'}>Travel Insurance</div>
              <div className={'header-title'}>About the travellers</div>
            </div>

            <InfoAnnie title={'Thanks for confirming your details.'}
                       text={'To complete your quote, we need some information about the travellers.'}/>

            <LeadTravellerDetails {...this.props}/>

            <ImportantStatements quotationDeclaration={quotationDeclaration} {...this.props}/>

            <HazardousActivities options={options} wordingUrl={this.getWordingUrl()} {...this.props} />

            <MedicalHistory medicalHistory={medicalHistory} {...this.props}/>

            <div style={{ marginTop: '50px' }}>
              <QuoteStepButtons
                submitting={submitting}
                hasBackButton={true}
                submitFailed={submitFailed}
                handleBack={onBack}
                disable={disableNext}
                submitButtonType={'validateButton'}
                synchronousError={synchronousError}
                checkForErrors={true}
                submit={actions.submit}
              />
            </div>

          </div>
        </Form>
      </>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const quoteState = state.quote;
      const tripType = selector(state, 'tripType');
      const travellers = selector(state, 'travellers');
      const quotes = selector(state, 'quotes');
      const groupType = selector(state, 'groupType');
      const premiums = selector(state, 'premiums');
      const schemeId = selector(state, 'schemeId');
      const options = selector(state, 'options');
      const coverLevel = selector(state, 'coverLevel');
      const quotationDeclaration = selector(state, 'quotationDeclaration');
      const medicalHistory = selector(state, 'medicalHistory');
      const medicalDeclaration = selector(state, 'medicalDeclaration');
      const documents = selector(state, 'documents');
      const region = selector(state, 'region');
      const destinations = selector(state, 'destinations');
      const synchronousError = getFormSyncErrors('quote')(state);
      return {
        auth: state.auth,
        tripType,
        travellers,
        quotes,
        groupType,
        quoteState,
        premiums,
        options,
        coverLevel,
        schemeId,
        quotationDeclaration,
        medicalHistory,
        medicalDeclaration,
        documents,
        region,
        synchronousError,
        destinations,
      };
    },
    (dispatch) => ({
      actions: {
        submit: bindActionCreators(submit, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(AboutTheTravellers);
